import apiClient from './api';

/**
 * Menu Service
 * Handles menu loading and permission checking
 */

export const menuService = {
  /**
   * Get user's accessible menus with permissions
   * @returns {Promise<Array>} Array of menu items
   */
  async getUserMenus() {
    try {
      const response = await apiClient.get('/menus.php');
      
      if (response.data.success) {
        // Store in session for quick access
        sessionStorage.setItem('userMenus', JSON.stringify(response.data.data));
        return response.data.data.menus || [];
      }
      
      return [];
    } catch (error) {
      console.error('Error loading menus:', error);
      
      // Try to get from session storage as fallback
      const cached = sessionStorage.getItem('userMenus');
      if (cached) {
        const parsed = JSON.parse(cached);
        return parsed.menus || [];
      }
      
      return [];
    }
  },

  /**
   * Get flat list of all menus (including nested)
   * @param {Array} menus - Hierarchical menu structure
   * @returns {Array} Flat array of all menus
   */
  flattenMenus(menus) {
    let flat = [];
    
    menus.forEach(menu => {
      flat.push(menu);
      
      if (menu.children && menu.children.length > 0) {
        flat = flat.concat(this.flattenMenus(menu.children));
      }
    });
    
    return flat;
  },

  /**
   * Check if user has specific permission for a menu
   * @param {Array} menus - Menu array (can be hierarchical or flat)
   * @param {String} menuName - Name of the menu to check
   * @param {String} permission - Permission type: 'view', 'add', 'edit', 'delete'
   * @returns {Boolean} True if user has permission
   */
  checkPermission(menus, menuName, permission) {
    // Flatten menus if they're hierarchical
    const flatMenus = this.flattenMenus(menus);
    
    // Find the menu
    const menu = flatMenus.find(m => m.menu_name === menuName);
    
    if (!menu) {
      console.warn(`Menu "${menuName}" not found in user's menus`);
      return false;
    }
    
    // Check permission
    switch(permission.toLowerCase()) {
      case 'view':
      case 'read':
        return menu.can_view === 1 || menu.can_view === '1';
        
      case 'add':
      case 'create':
        return menu.can_add === 1 || menu.can_add === '1';
        
      case 'edit':
      case 'update':
        return menu.can_edit === 1 || menu.can_edit === '1';
        
      case 'delete':
      case 'remove':
        return menu.can_delete === 1 || menu.can_delete === '1';
        
      default:
        console.warn(`Unknown permission type: ${permission}`);
        return false;
    }
  },

  /**
   * Get menu by name
   * @param {Array} menus - Menu array
   * @param {String} menuName - Menu name to find
   * @returns {Object|null} Menu object or null
   */
  getMenu(menus, menuName) {
    const flatMenus = this.flattenMenus(menus);
    return flatMenus.find(m => m.menu_name === menuName) || null;
  },

  /**
   * Get all permissions for a menu
   * @param {Array} menus - Menu array
   * @param {String} menuName - Menu name
   * @returns {Object} Object with can_view, can_add, can_edit, can_delete
   */
  getMenuPermissions(menus, menuName) {
    const menu = this.getMenu(menus, menuName);
    
    if (!menu) {
      return {
        can_view: false,
        can_add: false,
        can_edit: false,
        can_delete: false
      };
    }
    
    return {
      can_view: menu.can_view === 1 || menu.can_view === '1',
      can_add: menu.can_add === 1 || menu.can_add === '1',
      can_edit: menu.can_edit === 1 || menu.can_edit === '1',
      can_delete: menu.can_delete === 1 || menu.can_delete === '1'
    };
  },

  /**
   * Clear cached menus
   */
  clearCache() {
    sessionStorage.removeItem('userMenus');
  },

  /**
   * Build menu tree from flat array
   * @param {Array} flatMenus - Flat array of menus
   * @returns {Array} Hierarchical menu structure
   */
  buildMenuTree(flatMenus) {
    const menuMap = {};
    const roots = [];
    
    // Create map and initialize children arrays
    flatMenus.forEach(menu => {
      menuMap[menu.menu_id] = { ...menu, children: [] };
    });
    
    // Build tree
    flatMenus.forEach(menu => {
      if (menu.parent_menu_id === null || menu.parent_menu_id === 0) {
        // Root level menu
        roots.push(menuMap[menu.menu_id]);
      } else {
        // Child menu
        if (menuMap[menu.parent_menu_id]) {
          menuMap[menu.parent_menu_id].children.push(menuMap[menu.menu_id]);
        }
      }
    });
    
    return roots;
  },

  /**
   * Filter menus by permission
   * @param {Array} menus - Menu array
   * @param {String} permission - Permission to filter by
   * @returns {Array} Filtered menus
   */
  filterByPermission(menus, permission) {
    const flatMenus = this.flattenMenus(menus);
    
    return flatMenus.filter(menu => 
      this.checkPermission([menu], menu.menu_name, permission)
    );
  }
};

export default menuService;
